import { useEffect, useState } from "react";
import { eventsAPI } from "../../lib/api";
import { Star, Plus, Trash2, Edit, X, MapPin, Calendar, Users } from "lucide-react";
import { toast } from "sonner";
import { Header } from '../../components/layout/Header';

const TIPOS = [
    { value: "show",          label: "Show" },
    { value: "ministracao",   label: "Ministração" },
    { value: "mesa_de_ester", label: "Mesa de Ester" },
    { value: "mentoria",      label: "Mentoria" },
    { value: "outro",         label: "Outro" },
];

const STATUS_OPTS = ["rascunho","publicado","encerrado","cancelado"];
const STATUS_COLORS = {
    rascunho: "bg-stone-700 text-stone-300",
    publicado: "bg-green-900/40 text-green-300",
    encerrado: "bg-blue-900/40 text-blue-300",
    cancelado: "bg-red-900/40 text-red-300",
};

const EMPTY = { title: "", tipo: "show", description: "", date: "", location: "", online_link: "", price: "", max_participants: "", status: "rascunho", segmento: "geral", image_url: "" };

function EventModal({ open, onClose, onSaved, editing }) {
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);
    useEffect(() => { setForm(editing ? { ...editing } : EMPTY); }, [editing, open]);

    const save = async () => {
        if (!form.title || !form.date) { toast.error("Título e data são obrigatórios"); return; }
        setSaving(true);
        try {
            if (editing) await eventsAPI.update(editing.id, form);
            else         await eventsAPI.create(form);
            toast.success(editing ? "Evento atualizado" : "Evento criado");
            onSaved(); onClose();
        } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
        finally { setSaving(false); }
    };

    if (!open) return null;
    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-stone-900 border border-stone-700 rounded-2xl w-full max-w-lg shadow-2xl max-h-[90vh] flex flex-col">
                <div className="p-5 border-b border-stone-800 flex items-center justify-between flex-shrink-0">
                    <h2 className="font-bold text-stone-100">{editing ? "Editar" : "Novo"} Evento</h2>
                    <button onClick={onClose} className="text-stone-500 hover:text-stone-300"><X className="w-4 h-4" /></button>
                </div>
                <div className="p-5 space-y-3 overflow-y-auto">
                    <div>
                        <label className="text-xs text-stone-400 block mb-1">Título *</label>
                        <input className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-sm text-stone-100 focus:outline-none focus:border-amber-600"
                            value={form.title} onChange={e => setForm(f => ({...f, title: e.target.value}))} />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-xs text-stone-400 block mb-1">Tipo</label>
                            <select className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-sm text-stone-100 focus:outline-none focus:border-amber-600"
                                value={form.tipo} onChange={e => setForm(f => ({...f, tipo: e.target.value}))}>
                                {TIPOS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="text-xs text-stone-400 block mb-1">Status</label>
                            <select className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-sm text-stone-100 focus:outline-none focus:border-amber-600"
                                value={form.status} onChange={e => setForm(f => ({...f, status: e.target.value}))}>
                                {STATUS_OPTS.map(s => <option key={s} value={s}>{s}</option>)}
                            </select>
                        </div>
                    </div>
                    <div>
                        <label className="text-xs text-stone-400 block mb-1">Data *</label>
                        <input type="datetime-local" className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-sm text-stone-100 focus:outline-none focus:border-amber-600"
                            value={form.date} onChange={e => setForm(f => ({...f, date: e.target.value}))} />
                    </div>
                    <div>
                        <label className="text-xs text-stone-400 block mb-1">Local</label>
                        <input className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-sm text-stone-100 focus:outline-none focus:border-amber-600"
                            value={form.location} onChange={e => setForm(f => ({...f, location: e.target.value}))} placeholder="Cidade, local ou 'Online'" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-xs text-stone-400 block mb-1">Preço (R$)</label>
                            <input type="number" className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-sm text-stone-100 focus:outline-none focus:border-amber-600"
                                value={form.price} onChange={e => setForm(f => ({...f, price: e.target.value}))} placeholder="0 = gratuito" />
                        </div>
                        <div>
                            <label className="text-xs text-stone-400 block mb-1">Vagas</label>
                            <input type="number" className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-sm text-stone-100 focus:outline-none focus:border-amber-600"
                                value={form.max_participants} onChange={e => setForm(f => ({...f, max_participants: e.target.value}))} placeholder="Ilimitado" />
                        </div>
                    </div>
                    <div>
                        <label className="text-xs text-stone-400 block mb-1">Descrição</label>
                        <textarea rows={3} className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-sm text-stone-100 focus:outline-none focus:border-amber-600 resize-none"
                            value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} />
                    </div>
                </div>
                <div className="p-5 border-t border-stone-800 flex justify-end gap-2 flex-shrink-0">
                    <button onClick={onClose} className="px-4 py-2 text-sm text-stone-400 hover:text-stone-200">Cancelar</button>
                    <button onClick={save} disabled={saving}
                        className="px-4 py-2 bg-amber-700 hover:bg-amber-600 text-white text-sm rounded-lg disabled:opacity-50 transition-colors">
                        {saving ? "Salvando..." : "Salvar"}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default function AppointmentsAdmin() {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modal, setModal] = useState(false);
    const [editing, setEditing] = useState(null);

    const load = async () => {
        setLoading(true);
        try { const r = await eventsAPI.list(); setEvents(r.data || []); }
        catch { toast.error("Erro ao carregar eventos"); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const del = async (id) => {
        if (!window.confirm("Excluir evento?")) return;
        try { await eventsAPI.delete(id); toast.success("Excluído"); load(); }
        catch { toast.error("Erro"); }
    };

    return (
        <>
        <Header title="Eventos" subtitle="Shows, ministrações, Mesa de Ester, mentorias" actions={<button onClick={() => setModal(true)} className="flex items-center gap-2 bg-amber-700 hover:bg-amber-600 text-white text-sm px-4 py-2 rounded-lg transition-colors"><Plus className="w-4 h-4" /> Novo Evento</button>} />
        <div className="p-6 space-y-5">
            <EventModal open={modal} onClose={() => { setModal(false); setEditing(null); }} onSaved={load} editing={editing} />

            {loading ? (
                <div className="text-stone-500 text-sm text-center py-16">Carregando...</div>
            ) : events.length === 0 ? (
                <div className="bg-stone-900 border border-stone-800 rounded-xl p-12 text-center">
                    <Star className="w-10 h-10 text-stone-700 mx-auto mb-3" />
                    <p className="text-stone-400 text-sm">Nenhum evento cadastrado.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {events.map(ev => (
                        <div key={ev.id} className="bg-stone-900 border border-stone-800 rounded-xl p-4 hover:border-stone-700 transition-all">
                            <div className="flex items-start justify-between mb-2">
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-semibold text-stone-100 truncate">{ev.title}</p>
                                    <p className="text-xs text-stone-500 mt-0.5">{TIPOS.find(t => t.value === ev.tipo)?.label}</p>
                                </div>
                                <span className={`text-xs px-2 py-0.5 rounded-full ml-2 flex-shrink-0 ${STATUS_COLORS[ev.status] || STATUS_COLORS.rascunho}`}>
                                    {ev.status}
                                </span>
                            </div>
                            <div className="space-y-1 text-xs text-stone-400">
                                {ev.date && (
                                    <div className="flex items-center gap-1.5">
                                        <Calendar className="w-3 h-3" />
                                        {new Date(ev.date).toLocaleDateString("pt-BR", { day:"2-digit", month:"short", year:"numeric" })}
                                    </div>
                                )}
                                {ev.location && (
                                    <div className="flex items-center gap-1.5">
                                        <MapPin className="w-3 h-3" />
                                        {ev.location}
                                    </div>
                                )}
                                <div className="flex items-center gap-1.5">
                                    <Users className="w-3 h-3" />
                                    {ev.participants_count || 0} inscritos
                                    {ev.max_participants && ` / ${ev.max_participants}`}
                                </div>
                            </div>
                            <div className="flex gap-2 mt-3">
                                <button onClick={() => { setEditing(ev); setModal(true); }}
                                    className="flex-1 text-xs py-1.5 border border-stone-700 rounded-lg text-stone-300 hover:text-stone-100 hover:bg-stone-800 transition-colors flex items-center justify-center gap-1">
                                    <Edit className="w-3 h-3" /> Editar
                                </button>
                                <button onClick={() => del(ev.id)}
                                    className="p-1.5 text-stone-500 hover:text-red-400 hover:bg-red-900/20 rounded-lg transition-colors">
                                    <Trash2 className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
        </>
    );
}
